import React, { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, ChevronDown } from "lucide-react";

import Button from "../app/Button";
import StateBottomSheet from "./bottom-sheets/StateBottomSheet";
import CityBottomSheet from "./bottom-sheets/CityBottomSheet";
import HospitalsListBottomSheet from "./bottom-sheets/HospitalsListBottomSheet";
import DoctorsListBottomSheet from "./bottom-sheets/DoctorsListBottomSheet";

import { City, State } from "src/types/location.types";
import { Doctor, Hospital } from "src/types/doctors.types";
import { useAppSelector } from "src/redux/hooks";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";

interface DoctorDetailsProps {
  title: string;
  description: string;
  onBack: () => void;
  onNext: (data: {
    state: string;
    city: string;
    hospital: string;
    doctorName: string;
    accessCode: string;
  }) => void;
  support_program_key?: string;
}

const DoctorDetails: React.FC<DoctorDetailsProps> = ({
  title,
  description,
  onBack,
  onNext,
  support_program_key,
}) => {
  const [showStateBottomSheet, setShowStateBottomSheet] = useState(false);
  const [showCityBottomSheet, setShowCityBottomSheet] = useState(false);
  const [showHospitalsListBottomSheet, setShowHospitalsListBottomSheet] =
    useState(false);
  const [showDoctorBottomSheet, setShowDoctorBottomSheet] = useState(false);

  const [state, setState] = useState<State | null>(null);
  const [city, setCity] = useState<City | null>(null);
  const [hospital, setHospital] = useState<Hospital | null>(null);
  const [doctor, setDoctor] = useState<Doctor | null>(null);

  const { updateProfile } = useAppSelector((state) => state.auth);

  const isValid = state && city && hospital && doctor;

  useEffect(() => {
    analytics.pageView(SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN);
  }, []);

  const handleContinue = () => {
    if (isValid) {
      // Event: DOCTOR_DETAILS_CAPTURED
      analytics.trackEvent(EVENT_NAMES.DOCTOR_DETAILS_CAPTURED, {
        state: state?.state_name,
        city: city?.city_name,
        hospital: hospital?.clinic_name,
        doctorName: doctor?.name,
        screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
        support_program_key: support_program_key,
      });

      onNext({
        state: state?.state_name ?? "",
        city: city?.city_name ?? "",
        hospital: hospital?.clinic_name ?? "",
        doctorName: doctor?.name ?? "",
        accessCode: doctor?.access_code ?? "",
      });
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="p-4 flex-1 min-h-0 overflow-y-auto">
        <button
          className="flex items-center gap-1 px-4 py-2 -ml-4"
          onClick={onBack}
        >
          <ArrowLeft className="w-4 h-4 text-gray-500" />
          <p className="text-sm text-gray-500">Back</p>
        </button>

        <div className="my-4">
          <h1 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
            {title}
          </h1>
          <p className="text-[var(--text-secondary)]">{description}</p>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mt-4 mb-2 block">
            Doctor's State
          </label>
          <button
            onClick={() => {
              // Event: DOCTOR_STATE_SELECTED_CLICKED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_STATE_INPUT_CLICKED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              // Event: SELECT_DOCTOR_STATE_BOTTOM_SHEET_OPENED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_STATE_BOTTOM_SHEET_OPENED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              setShowStateBottomSheet(true);
            }}
            className={`bg-white w-full border rounded px-4 py-3 text-sm text-left flex items-center justify-between ${
              state ? "text-gray-700" : "text-gray-500"
            }`}
            type="button"
          >
            {state?.state_name || "Select Doctor's State"}
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          <label className="text-sm font-medium text-gray-700 mt-4 mb-2 block">
            Doctor's City
          </label>
          <button
            onClick={() => {
              // Event: DOCTOR_CITY_INPUT_CLICKED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_CITY_INPUT_CLICKED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              // Event: SELECT_DOCTOR_CITY_BOTTOM_SHEET_OPENED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_CITY_BOTTOM_SHEET_OPENED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              setShowCityBottomSheet(true);
            }}
            className={`bg-white w-full border rounded px-4 py-3 text-sm text-left flex items-center justify-between ${
              city ? "text-gray-700" : "text-gray-500"
            }`}
            type="button"
          >
            {city?.city_name || "Select Doctor's City"}
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          <label className="text-sm font-medium text-gray-700 mt-4 mb-2 block">
            Hospital
          </label>
          <button
            onClick={() => {
              // Event: DOCTOR_HOSPITAL_INPUT_CLICKED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_HOSPITAL_INPUT_CLICKED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              // Event: SELECT_DOCTOR_HOSPITAL_BOTTOM_SHEET_OPENED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_HOSPITAL_BOTTOM_SHEET_OPENED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              setShowHospitalsListBottomSheet(true);
            }}
            className={`bg-white w-full border rounded px-4 py-3 text-sm text-left flex items-center justify-between ${
              hospital ? "text-gray-700" : "text-gray-500"
            }`}
            type="button"
          >
            {hospital?.clinic_name || "Select Hospital"}
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          <label className="text-sm font-medium text-gray-700 mt-4 mb-2 block">
            Doctor
          </label>
          <button
            onClick={() => {
              if (!hospital) return;
              // Event: DOCTOR_INPUT_CLICKED
              analytics.trackEvent(EVENT_NAMES.SELECT_DOCTOR_INPUT_CLICKED, {
                screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                support_program_key: support_program_key,
              });

              // Event: SELECT_DOCTOR_BOTTOM_SHEET_OPENED
              analytics.trackEvent(
                EVENT_NAMES.SELECT_DOCTOR_BOTTOM_SHEET_OPENED,
                {
                  screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                  support_program_key: support_program_key,
                },
              );

              setShowDoctorBottomSheet(true);
            }}
            disabled={!hospital}
            className={`bg-white w-full border rounded px-4 py-3 text-sm text-left flex items-center justify-between ${
              doctor?.name ? "text-gray-700" : "text-gray-500"
            } ${!hospital ? "opacity-50 cursor-not-allowed" : ""}`}
            type="button"
          >
            {doctor?.name || "Select Doctor"}
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>

      <div className="bg-white border-t border-gray-200 p-4 mt-auto">
        <Button
          fullWidth
          onClick={handleContinue}
          disabled={!isValid || updateProfile.isLoading}
          loading={updateProfile.isLoading}
        >
          <span className="flex items-center gap-2">
            Continue
            <ArrowRight className="w-4 h-4" />
          </span>
        </Button>
      </div>

      {showStateBottomSheet && (
        <StateBottomSheet
          show={showStateBottomSheet}
          handleClose={() => {
            // Event: SELECT_DOCTOR_STATE_BOTTOM_SHEET_CLOSED
            analytics.trackEvent(
              EVENT_NAMES.SELECT_DOCTOR_STATE_BOTTOM_SHEET_CLOSED,
              {
                screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                support_program_key: support_program_key,
              },
            );
            setShowStateBottomSheet(false);
          }}
          onSelect={(state) => {
            // Event: SELECT_DOCTOR_STATE_BOTTOM_SHEET_ITEM_CLICKED
            analytics.trackEvent(EVENT_NAMES.DOCTOR_STATE_SELECTED, {
              screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
              support_program_key: support_program_key,
              state: state?.state_name,
            });
            setState(state);
            setCity(null);
            setHospital(null);
            setDoctor(null);
          }}
        />
      )}

      {showCityBottomSheet && (
        <CityBottomSheet
          show={showCityBottomSheet}
          handleClose={() => {
            // Event: SELECT_DOCTOR_CITY_BOTTOM_SHEET_CLOSED
            analytics.trackEvent(
              EVENT_NAMES.SELECT_DOCTOR_CITY_BOTTOM_SHEET_CLOSED,
              {
                screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                support_program_key: support_program_key,
              },
            );
            setShowCityBottomSheet(false);
          }}
          onSelect={(city) => {
            // Event: SELECT_DOCTOR_CITY_BOTTOM_SHEET_ITEM_CLICKED
            analytics.trackEvent(EVENT_NAMES.DOCTOR_CITY_SELECTED, {
              screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
              support_program_key: support_program_key,
              city: city?.city_name,
            });
            setCity(city);
            setHospital(null);
            setDoctor(null);
          }}
          stateId={state?.state_master_id || ""}
        />
      )}

      {showHospitalsListBottomSheet && (
        <HospitalsListBottomSheet
          show={showHospitalsListBottomSheet}
          handleClose={() => {
            // Event: SELECT_DOCTOR_HOSPITAL_BOTTOM_SHEET_CLOSED
            analytics.trackEvent(
              EVENT_NAMES.SELECT_DOCTOR_HOSPITAL_BOTTOM_SHEET_CLOSED,
              {
                screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                support_program_key: support_program_key,
              },
            );
            setShowHospitalsListBottomSheet(false);
          }}
          onSelect={(hospital) => {
            // Event: SELECT_DOCTOR_HOSPITAL_BOTTOM_SHEET_ITEM_CLICKED
            analytics.trackEvent(EVENT_NAMES.DOCTOR_HOSPITAL_SELECTED, {
              screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
              support_program_key: support_program_key,
              hospital: hospital?.clinic_name,
            });
            setHospital(hospital);
            setDoctor(null); // Reset doctor when hospital changes
          }}
          cityId={city?.city_master_id || ""}
        />
      )}

      {showDoctorBottomSheet && (
        <DoctorsListBottomSheet
          show={showDoctorBottomSheet}
          stateId={state?.state_master_id || ""}
          stateName={state?.state_name || ""}
          cityId={city?.city_master_id || ""}
          cityName={city?.city_name || ""}
          clinicId={hospital?.clinic_master_id || ""}
          handleClose={() => {
            // Event: SELECT_DOCTOR_BOTTOM_SHEET_CLOSED
            analytics.trackEvent(
              EVENT_NAMES.SELECT_DOCTOR_BOTTOM_SHEET_CLOSED,
              {
                screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
                support_program_key: support_program_key,
              },
            );
            setShowDoctorBottomSheet(false);
          }}
          onSelect={(doctor) => {
            // Event: SELECT_DOCTOR_BOTTOM_SHEET_ITEM_CLICKED
            analytics.trackEvent(EVENT_NAMES.DOCTOR_SELECTED, {
              screen_name: SCREEN_NAMES.DOCTOR_DETAILS_CAPTURE_SCREEN,
              support_program_key: support_program_key,
              doctor: doctor?.name,
            });
            setDoctor(doctor);
          }}
        />
      )}
    </div>
  );
};

export default DoctorDetails;
